/**
 * Wave 1 S02 (2026-06-08) · `.buddy-note` — Buddy возвращается в тикеты.
 *
 * Markup contract (per sprint-02.ts):
 *   <div class='buddy-note' data-state='hint'>
 *     <div class='bn-bubble'>
 *       <div class='bn-tag'>Buddy · совет</div>
 *       <div class='bn-text'>…реплика…</div>
 *     </div>
 *   </div>
 *
 * Контент держит ТОЛЬКО текст реплики и состояние. Маскот НЕ зашивается в
 * html: вариант приходит из дневной ротации (rotatingBuddyVariant — «один
 * день — один персонаж», Genie 7 JUN), поэтому слот `.bn-slot` создаётся
 * программно и в него рендерится живой <BuddyMascot/> через createRoot.
 * `.praise`-модификатор (зелёный бабл) задаётся классом в разметке.
 *
 * Cleanup: unmount отложен через setTimeout — синхронный unmount из
 * useEffect-cleanup родителя ругается в React 18.
 */
import { createRoot, type Root } from 'react-dom/client';
import { BuddyMascot, type BuddyState } from '@/components/buddies';

const VALID_STATES: ReadonlySet<string> = new Set(['idle', 'hint', 'happy', 'sad', 'sleep']);

export function initBuddyNotes(root: HTMLElement): () => void {
  const notes = Array.from(root.querySelectorAll<HTMLElement>('.buddy-note'));
  const cleanups: Array<() => void> = [];

  for (const note of notes) {
    if (note.dataset.bnInit === '1') continue;
    note.dataset.bnInit = '1';

    const raw = note.getAttribute('data-state') ?? 'idle';
    const state = (VALID_STATES.has(raw) ? raw : 'idle') as BuddyState;

    const slot = document.createElement('div');
    slot.className = 'bn-slot';
    slot.setAttribute('aria-hidden', 'true'); // декоративный персонаж · текст несёт .bn-text
    note.prepend(slot);

    let reactRoot: Root | null = createRoot(slot);
    reactRoot.render(<BuddyMascot size={86} state={state} />);

    cleanups.push(() => {
      const r = reactRoot;
      reactRoot = null;
      setTimeout(() => {
        r?.unmount();
        slot.remove();
      }, 0);
      delete note.dataset.bnInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
